﻿import { useEffect, useState, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from 'Store/selectors'
import { useSettings } from 'Contexts/SettingsContext'
import { setStoreFileType } from 'Application/Visits/Actions/reducer'
import Const from 'Utils/Consts'
import NewVisitHeader from './NewVisitHeader'
import AccessDenied from './AccessDenied'
import GetDeliberatelyPage from './GetDeliberatelyPage'
import VehicleVisitingPointsMode1Page from './VehicleVisitingPointsMode1Page'
import VehicleVisitingPointsMode2Page from './VehicleVisitingPointsMode2Page'
import ImagesCheck from './ImagesCheck'
import GetCarInfoPage from './GetCarInfoPage'
import GetCustomerInfoPage from './GetCustomerInfoPage'
import GetOptionsPage from './GetOptionsPage'
import NotHealthyAutoPartsPage from './NotHealthyAutoPartsPage'
import VehicleAccessoriesPage from './VehicleAccessoriesPage'
import GetPricePage from './GetPricePage'
import GetDescriptionPage from './GetDescriptionPage'
import { useGetInsuranceExpertFieldsCurrentUserQuery, useGetUserProfileQuery } from 'Services/api'

const NewVisitPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0)

  const dispatch = useAppDispatch()
  const { settings } = useSettings()

  const isSaved = useAppSelector(s => s.visits.newVisit.isSaved)
  const carInfo = useAppSelector(s => s.visits.newVisit.carInfo)

  const { data: userProfileData } = useGetUserProfileQuery()
  const { data: expertData } = useGetInsuranceExpertFieldsCurrentUserQuery()

  const takePictureMode =
    settings?.REACT_APP_TAKE_PICTURES_MODE ?? Const.env.REACT_APP_TAKE_PICTURES_MODE
  const storeFileType = settings?.REACT_APP_STORE_FILE_TYPE ?? Const.env.REACT_APP_STORE_FILE_TYPE

  // initialize storeFileType if not saved
  useEffect(() => {
    if (!isSaved) {
      dispatch(setStoreFileType(storeFileType))
    }
  }, [isSaved, storeFileType, dispatch])

  // skip NotHealthyAutoPartsPage if vehicleStatus == 1
  useEffect(() => {
    if (activeStep === 7 && carInfo.vehicleStatus === 1) {
      setActiveStep(8)
    }
  }, [activeStep, carInfo.vehicleStatus])

  const hasAccess = useCallback((): number => {
    if (userProfileData && expertData) {
      if (userProfileData.errorMessages?.length > 0) return 2

      const insurance = expertData.find(x => x.insuranceTypeCoreId === 44)
      if (insurance) {
        if (!insurance.signatureImageId) return 1
        if (insurance.operationType === 0 || insurance.operationType === 2) return 10
      }
    }
    return 0
  }, [expertData, userProfileData])

  const handleNext = () => {
    setActiveStep(prev => {
      let newStep = prev + 1
      if (prev === 6 && carInfo.vehicleStatus === 1) newStep += 1
      return newStep
    })
  }

  const handleBack = () => {
    setActiveStep(prev => {
      let newStep = prev - 1
      if (prev === 8 && carInfo.vehicleStatus === 1) newStep -= 1
      return newStep
    })
  }

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <GetDeliberatelyPage handleNext={handleNext} />
      case 1:
        return takePictureMode === 'MODE1' ? (
          <VehicleVisitingPointsMode1Page handleNext={handleNext} handleBack={handleBack} />
        ) : (
          <VehicleVisitingPointsMode2Page handleNext={handleNext} handleBack={handleBack} />
        )
      case 2:
        return <ImagesCheck handleNext={handleNext} handleBack={handleBack} />
      case 3:
        return <VehicleAccessoriesPage handleNext={handleNext} handleBack={handleBack} />
      case 4:
        return <GetCarInfoPage handleNext={handleNext} handleBack={handleBack} />
      case 5:
        return <GetCustomerInfoPage handleNext={handleNext} handleBack={handleBack} />
      case 6:
        return <GetOptionsPage handleNext={handleNext} handleBack={handleBack} />
      case 7:
        return <NotHealthyAutoPartsPage handleNext={handleNext} handleBack={handleBack} />
      // case 7:
      //   return <GetCoveragesPage handleNext={handleNext} handleBack={handleBack} />
      case 8:
        return <GetPricePage handleNext={handleNext} handleBack={handleBack} />
      case 9:
        return <GetDescriptionPage handleBack={handleBack} isFake={false} />
      default:
        return <GetCarInfoPage handleNext={handleNext} handleBack={handleBack} />
    }
  }

  return hasAccess() === 10 ? (
    <>
      <NewVisitHeader activeStep={activeStep} />
      {renderStep()}
    </>
  ) : (
    <AccessDenied messageType={hasAccess()} />
  )
}

export default NewVisitPage
